import React, { Component } from 'react';
import Inherit from '../../core/inherit';
import withNavigate from '../../core/withNavigate';
import AuditConsoleController from './auditConsole.controller';
import { Layout, PageHeader, SectionCard, FilterBar, FilterField, StatusTag, EmptyState } from '../common';
import { AnalyticsSkeleton, TableSkeleton } from '../common/skeletons';
import { formatPriceName } from '../../utils/priceName';
import { Tabs, Table, Button, Input, InputNumber, Select, Space, Tooltip, Breadcrumb, Popconfirm, Skeleton } from 'antd';
import {
    ClipboardCheck,
    Search,
    ChartColumn,
    Database,
    ScanBarcode,
    Store,
    Users,
    ListChecks,
    Copy,
    CopyCheck,
    Download,
    Trash2,
    Pencil,
    Archive,
    Check,
    X,
    Clock,
    Tags,
    Info,
    Eye,
    Keyboard,
    RotateCw,
    TriangleAlert,
    ArchiveRestore,
    ArrowLeftRight,
} from 'lucide-react';
import { fmtVZ } from '../../utils/timeUtils';
import AuditProductDrawer from './elements/AuditProductDrawer';
import {
    AuditImageThumb,
    AuditStatusBadge,
    AuditFlagChips,
    BarcodeIcon,
    AuditRecordDrawer,
    AuditReviewCard,
    AuditStoreCheckDrawer,
    AuditAuditorDrawer,
    AuditMergeModal,
    AuditDuplicateGroups,
    AuditAnalyticsPanel,
    AuditorRankings,
    AuditPriceDisplay,
    AuditFilterToggle,
} from './elements';

const PAGE_SIZES = [25, 50, 100];

/** Column title with an info tooltip (used for the visit counters). */
const HelpTitle = ({ title, tooltip }) => (
    <Tooltip title={tooltip}>
        <span className="inline-flex items-center gap-1">
            {title}
            <Info className="text-surface-400 text-xs" />
        </span>
    </Tooltip>
);

/** Quiet danger icon button wrapped in a confirm (row deletes / archive). */
const ConfirmIconButton = ({ tooltip, icon, ...confirmProps }) => (
    <Popconfirm okType="danger" cancelText="Cancelar" getPopupContainer={() => document.body} {...confirmProps}>
        <Tooltip title={tooltip}>
            <Button type="text" size="small" danger icon={icon} aria-label={tooltip} onClick={(e) => e.stopPropagation()} />
        </Tooltip>
    </Popconfirm>
);

class ShelfAuditConsole extends Inherit(AuditConsoleController, Component) {
    // Read-only without "Gestionar auditoría" (Análisis Auditor is also limited to visits +
    // scans by auditors).
    get readOnly() {
        return !this.canManageAudit;
    }

    renderSummaryTab() {
        const { analytics, analyticsLoading } = this.state;
        if (analyticsLoading && !analytics) return <AnalyticsSkeleton cards={6} />;
        if (!analytics) {
            return (
                <div className="shelf-card">
                    <EmptyState
                        icon={<ChartColumn />}
                        title="Sin datos de analíticas"
                        description="No se pudieron cargar las métricas de la consola."
                        action={
                            <Button icon={<RotateCw />} onClick={() => this.loadAnalytics()}>
                                Reintentar
                            </Button>
                        }
                    />
                </div>
            );
        }
        return (
            <AuditAnalyticsPanel
                analytics={analytics}
                loading={analyticsLoading}
                onRefresh={() => this.loadAnalytics()}
                onVisitClick={(v) => this.openStoreCheckDrawer(v)}
                onAuditorClick={(id) => this.openAuditorDrawer(id)}
                navigateKpiFilter={this.navigateKpiFilter}
                onReviewQueueClick={() => this.setTabKey('review')}
            />
        );
    }

    renderProductsTab() {
        const {
            products,
            productsLoading,
            productsPagination,
            productSearch,
            productStatusFilter,
            productReviewStatusFilter,
            productMissingFilter,
            productDuplicatesFilter,
            productInvalidCodesFilter,
            productShowArchived,
            productCategoryFilter,
            productSegmentFilter,
            productCountryFilter,
            productGramsMin,
            productGramsMax,
            productPriceMin,
            productPriceMax,
            productFacets,
            showDuplicatesPanel,
            duplicateGroups,
            duplicatesLoading,
            deleting,
        } = this.state;

        const productsColumns = [
            {
                title: 'Foto',
                width: 72,
                render: (_, row) => (
                    <AuditImageThumb
                        record={row.latestScan || { imageCount: 0 }}
                        onClick={row.latestScan?._id ? () => this.openRecordDrawer(row.latestScan) : undefined}
                    />
                ),
            },
            {
                // Widths below are explicit on purpose: every other column is fixed, so a flexible
                // "Nombre" gets squeezed to nothing once they add up past the table width. Names
                // wrap instead of truncating — this is the column people actually read.
                title: 'Nombre',
                dataIndex: 'name',
                width: 280,
                render: (t, r) => <span className="text-sm font-medium text-surface-900">{formatPriceName(r)}</span>,
            },
            {
                title: 'Código',
                dataIndex: 'code',
                width: 160,
                render: (code, r) => (
                    <div className="space-y-1">
                        <div className="flex items-center gap-1.5">
                            <BarcodeIcon barcodeMeta={r.barcodeMeta} hasCatalogMatch />
                            <span className="text-sm tabular-nums">{code}</span>
                        </div>
                        {!r.barcodeMeta?.valid && <StatusTag tone="danger">Código inválido</StatusTag>}
                        {['suspected', 'confirmed'].includes(r.duplicateStatus) && (
                            <StatusTag tone="warning" icon={<Copy />}>
                                Posible duplicado
                            </StatusTag>
                        )}
                    </div>
                ),
            },
            { title: 'Categoría', dataIndex: 'category', width: 130, render: (t) => <span className="text-sm text-surface-700">{t}</span> },
            { title: 'Segmento', dataIndex: 'segment', width: 150, render: (t) => <span className="text-sm text-surface-700">{t}</span> },
            {
                title: 'Estado',
                dataIndex: 'status',
                width: 130,
                render: (s, row) => (
                    <Space direction="vertical" size={4}>
                        <AuditStatusBadge type="product" status={s} />
                        {row.active === false && (
                            <StatusTag tone="neutral" icon={<Archive />}>
                                Archivado
                            </StatusTag>
                        )}
                    </Space>
                ),
            },
            {
                title: 'Revisión',
                dataIndex: 'reviewStatus',
                width: 140,
                render: (rs) => <AuditStatusBadge type="review" status={rs || 'unreviewed'} />,
            },
            {
                title: 'Datos mín.',
                dataIndex: 'missingData',
                width: 100,
                render: (v) => (v ? <StatusTag tone="danger">Sí</StatusTag> : <StatusTag tone="neutral">No</StatusTag>),
            },
            {
                title: 'Actualizado',
                dataIndex: 'updatedAt',
                width: 160,
                render: (d) => <span className="text-xs text-surface-500 tabular-nums">{fmtVZ(d)}</span>,
            },
            {
                title: '',
                key: 'act',
                width: 90,
                align: 'right',
                render: (_, row) => (
                    <div className="flex items-center justify-end gap-0.5" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Editar">
                            <Button type="text" size="small" icon={<Pencil />} aria-label="Editar" onClick={() => this.openProductDrawer(row)} />
                        </Tooltip>
                        <ConfirmIconButton
                            tooltip="Archivar"
                            icon={<Archive />}
                            title="¿Archivar producto?"
                            description={`${row.name} (${row.code}) se desactivará del catálogo.`}
                            onConfirm={() => this._deleteProduct(row._id, false)}
                            okText="Sí, archivar"
                            okButtonProps={{ loading: deleting }}
                        />
                    </div>
                ),
            },
        ];

        return (
            <div className="space-y-4">
                <FilterBar
                    className="!mb-0"
                    actions={
                        <>
                            <Button onClick={() => this.clearProductFilters()}>Limpiar</Button>
                            <Button type="primary" icon={<Search />} onClick={() => this.loadProducts(1)}>
                                Buscar
                            </Button>
                        </>
                    }>
                    <FilterField label="Buscar" className="w-full sm:w-72">
                        <Input
                            allowClear
                            placeholder="Buscar por nombre o código de barras…"
                            prefix={<Search className="text-surface-400" />}
                            value={productSearch}
                            onChange={(e) => this.setProductSearch(e.target.value)}
                            onPressEnter={() => this.loadProducts(1)}
                        />
                    </FilterField>
                    <FilterField label="Estado" className="w-[170px]">
                        <Select
                            allowClear
                            placeholder="Todos"
                            value={productStatusFilter}
                            onChange={(v) => this.setProductStatusFilter(v)}
                            options={[
                                { value: 'complete', label: 'Completo' },
                                { value: 'pending_enrichment', label: 'Pendiente ajuste' },
                            ]}
                        />
                    </FilterField>
                    <FilterField label="Revisión" className="w-[170px]">
                        <Select
                            allowClear
                            placeholder="Todas"
                            value={productReviewStatusFilter}
                            onChange={(v) => this.setProductReviewStatusFilter(v)}
                            options={[
                                { value: 'unreviewed', label: 'Pendiente' },
                                { value: 'approved', label: 'Aprobado' },
                                { value: 'needs_fix', label: 'Requiere ajuste' },
                                { value: 'rejected', label: 'Rechazado' },
                            ]}
                        />
                    </FilterField>
                    <FilterField label="Datos mínimos" className="w-[150px]">
                        <Select
                            allowClear
                            placeholder="Todos"
                            value={productMissingFilter}
                            onChange={(v) => this.setProductMissingFilter(v)}
                            options={[
                                { value: 'true', label: 'Sí' },
                                { value: 'false', label: 'No' },
                            ]}
                        />
                    </FilterField>
                    <FilterField label="Categoría" className="w-[170px]">
                        <Select
                            allowClear
                            showSearch
                            placeholder="Todas"
                            value={productCategoryFilter}
                            onChange={(v) => this.setProductCategoryFilter(v)}
                            optionFilterProp="label"
                            options={(productFacets?.categories || []).map((c) => ({ value: c, label: c }))}
                        />
                    </FilterField>
                    <FilterField label="Segmento" className="w-[170px]">
                        <Select
                            allowClear
                            showSearch
                            placeholder="Todos"
                            value={productSegmentFilter}
                            onChange={(v) => this.setProductSegmentFilter(v)}
                            optionFilterProp="label"
                            options={(productFacets?.segments || []).map((s) => ({ value: s, label: s }))}
                        />
                    </FilterField>
                    <FilterField label="País" className="w-[150px]">
                        <Select
                            allowClear
                            showSearch
                            placeholder="Todos"
                            value={productCountryFilter}
                            onChange={(v) => this.setProductCountryFilter(v)}
                            optionFilterProp="label"
                            options={(productFacets?.countries || []).map((c) => ({ value: c, label: c }))}
                        />
                    </FilterField>
                    <FilterField label="Gramos" className="min-w-0">
                        <Space.Compact>
                            <InputNumber
                                placeholder="Gramos mín"
                                className="w-[110px]"
                                min={0}
                                value={productGramsMin}
                                onChange={(v) => this.setProductGramsMin(v)}
                                onPressEnter={() => this.loadProducts(1)}
                            />
                            <InputNumber
                                placeholder="máx"
                                className="w-[90px]"
                                min={0}
                                value={productGramsMax}
                                onChange={(v) => this.setProductGramsMax(v)}
                                onPressEnter={() => this.loadProducts(1)}
                            />
                        </Space.Compact>
                    </FilterField>
                    <FilterField label="Precio" className="min-w-0">
                        <Space.Compact>
                            <InputNumber
                                placeholder="Precio mín"
                                className="w-[110px]"
                                min={0}
                                value={productPriceMin}
                                onChange={(v) => this.setProductPriceMin(v)}
                                onPressEnter={() => this.loadProducts(1)}
                            />
                            <InputNumber
                                placeholder="máx"
                                className="w-[90px]"
                                min={0}
                                value={productPriceMax}
                                onChange={(v) => this.setProductPriceMax(v)}
                                onPressEnter={() => this.loadProducts(1)}
                            />
                        </Space.Compact>
                    </FilterField>
                    <FilterField label="Mostrar solo" className="min-w-0">
                        <div className="flex flex-wrap gap-2">
                            <AuditFilterToggle
                                active={productDuplicatesFilter}
                                icon={<Copy />}
                                onClick={() => this.setProductDuplicatesFilter(!productDuplicatesFilter)}>
                                Posibles duplicados
                            </AuditFilterToggle>
                            <AuditFilterToggle
                                active={productInvalidCodesFilter}
                                icon={<TriangleAlert />}
                                onClick={() => this.setProductInvalidCodesFilter(!productInvalidCodesFilter)}>
                                Códigos inválidos
                            </AuditFilterToggle>
                            <AuditFilterToggle
                                active={productShowArchived}
                                icon={<ArchiveRestore />}
                                onClick={() => this.setProductShowArchived(!productShowArchived)}>
                                Mostrar archivados
                            </AuditFilterToggle>
                        </div>
                    </FilterField>
                </FilterBar>

                {showDuplicatesPanel && (
                    <SectionCard
                        title="Grupos de duplicados"
                        description="Fusiona o descarta productos que parecen ser el mismo SKU"
                        icon={<Copy />}
                        iconTone="warning"
                        flush
                        extra={
                            <Tooltip title="Cerrar panel">
                                <Button type="text" size="small" icon={<X />} aria-label="Cerrar panel" onClick={() => this.hideDuplicatesPanel()} />
                            </Tooltip>
                        }>
                        <AuditDuplicateGroups
                            groups={duplicateGroups}
                            loading={duplicatesLoading}
                            onMergeGroup={this.openMergeModal}
                            onMarkDuplicate={this.markAsDuplicate.bind(this)}
                            onRecordClick={this.openRecordDrawer}
                            onDismiss={() => this.hideDuplicatesPanel()}
                        />
                    </SectionCard>
                )}

                {productsLoading && !products.length ? (
                    <TableSkeleton rows={8} columns={8} />
                ) : (
                    <SectionCard
                        title="Productos auditados"
                        description={`${productsPagination.total ?? products.length} productos en el catálogo de auditoría`}
                        icon={<Database />}
                        flush
                        extra={
                            <Button icon={<CopyCheck />} onClick={() => this.loadDuplicates()}>
                                Detectar duplicados
                            </Button>
                        }>
                        <Table
                            rowKey="_id"
                            size="middle"
                            loading={productsLoading}
                            dataSource={products}
                            columns={productsColumns}
                            // Must cover the sum of the column widths (1412), otherwise Ant shrinks
                            // the columns to fit and the widest text column loses.
                            scroll={{ x: 1412 }}
                            locale={{
                                emptyText: (
                                    <EmptyState icon={<Database />} title="Sin productos" description="Ningún producto coincide con los filtros aplicados" />
                                ),
                            }}
                            pagination={{
                                current: productsPagination.page,
                                pageSize: productsPagination.limit,
                                total: productsPagination.total,
                                showSizeChanger: true,
                                pageSizeOptions: PAGE_SIZES,
                                onChange: (page, pageSize) => this.changeProductsPage(page, pageSize),
                            }}
                        />
                    </SectionCard>
                )}
            </div>
        );
    }

    renderRecordsTab() {
        const readOnly = this.readOnly;
        const {
            records,
            recordsLoading,
            recordsPagination,
            recordCodeFilter,
            recordAuditorFilter,
            recordUserTypeFilter,
            auditorsList,
            selectedRecordIds,
            deleting,
            correctingCurrency,
        } = this.state;

        const recordsColumns = [
            {
                title: 'Imagen',
                width: 72,
                render: (_, r) => <AuditImageThumb record={r} onClick={() => this.openRecordDrawer(r)} />,
            },
            {
                title: 'Código',
                dataIndex: 'code',
                width: 180,
                render: (code, r) => (
                    <div className="space-y-1">
                        <div className="flex items-center gap-1.5">
                            <BarcodeIcon barcodeMeta={r.barcodeMeta} hasCatalogMatch={!!r.auditProduct} />
                            <span className="text-sm font-medium tabular-nums">{code}</span>
                        </div>
                        <AuditFlagChips flags={r.flags} barcodeMeta={r.barcodeMeta} showBarcode={false} />
                    </div>
                ),
            },
            {
                title: 'Precio',
                width: 130,
                align: 'right',
                render: (_, r) => (
                    <AuditPriceDisplay
                        price={r.price}
                        currency={r.currency}
                        priceUsd={r.priceUsd}
                        priceConversion={r.priceConversion}
                        isPromo={r.isPromo}
                        promoPrice={r.promoPrice}
                        promoPriceUsd={r.promoPriceUsd}
                        promoPriceConversion={r.promoPriceConversion}
                    />
                ),
            },
            {
                title: 'Promo',
                width: 80,
                render: (_, r) => (r.isPromo ? <StatusTag tone="violet">Sí</StatusTag> : <span className="text-surface-400">—</span>),
            },
            { title: 'Tienda', ellipsis: true, render: (_, r) => <span className="text-sm text-surface-700">{r.storeId?.name || '—'}</span> },
            { title: 'Auditor', ellipsis: true, render: (_, r) => <span className="text-sm text-surface-700">{r.userId?.name || '—'}</span> },
            {
                title: 'Imágenes',
                width: 120,
                render: (_, r) => <AuditStatusBadge type="image" status={r.imageProcessingStatus} error={r.imageProcessingError} />,
            },
            { title: 'Fecha', width: 150, render: (_, r) => <span className="text-xs text-surface-500 tabular-nums">{fmtVZ(r.createdAt)}</span> },
            !readOnly && {
                title: '',
                width: 56,
                align: 'right',
                render: (_, r) => (
                    <ConfirmIconButton
                        tooltip="Eliminar escaneo"
                        icon={<Trash2 />}
                        title="¿Eliminar escaneo?"
                        description={`Se borrará permanentemente el código ${r.code}.`}
                        onConfirm={() => this._deleteRecord(r._id)}
                        okText="Sí, eliminar"
                    />
                ),
            },
        ].filter(Boolean);

        const bulkActions = !readOnly && selectedRecordIds.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-surface-500 tabular-nums">{selectedRecordIds.length} seleccionados</span>
                <Popconfirm
                    title={`¿Corregir moneda a Bolívares en ${selectedRecordIds.length} escaneo(s)?`}
                    onConfirm={() => this._bulkCorrectCurrency(selectedRecordIds, 'bs')}
                    okText="Sí, Bolívares"
                    cancelText="Cancelar"
                    getPopupContainer={() => document.body}>
                    <Button size="small" icon={<ArrowLeftRight />} loading={correctingCurrency}>
                        Corregir a Bolívares ({selectedRecordIds.length})
                    </Button>
                </Popconfirm>
                <Popconfirm
                    title={`¿Corregir moneda a Dólares en ${selectedRecordIds.length} escaneo(s)?`}
                    onConfirm={() => this._bulkCorrectCurrency(selectedRecordIds, 'dollars')}
                    okText="Sí, Dólares"
                    cancelText="Cancelar"
                    getPopupContainer={() => document.body}>
                    <Button size="small" icon={<ArrowLeftRight />} loading={correctingCurrency}>
                        Corregir a Dólares ({selectedRecordIds.length})
                    </Button>
                </Popconfirm>
                <Popconfirm
                    title={`¿Eliminar ${selectedRecordIds.length} escaneo(s)?`}
                    description="Esta acción no se puede deshacer."
                    onConfirm={() => this._bulkDeleteRecords(selectedRecordIds)}
                    okText="Sí, eliminar"
                    okType="danger"
                    cancelText="Cancelar"
                    getPopupContainer={() => document.body}>
                    <Button size="small" danger icon={<Trash2 />} loading={deleting}>
                        Eliminar ({selectedRecordIds.length})
                    </Button>
                </Popconfirm>
            </div>
        );

        return (
            <div className="space-y-4">
                <FilterBar
                    className="!mb-0"
                    actions={
                        <Button type="primary" icon={<Search />} onClick={() => this.loadRecords(1)}>
                            Buscar
                        </Button>
                    }>
                    <FilterField label="Código" className="w-full sm:w-[220px]">
                        <Input
                            allowClear
                            placeholder="Filtrar por código"
                            prefix={<ScanBarcode className="text-surface-400" />}
                            value={recordCodeFilter}
                            onChange={(e) => this.setRecordCodeFilter(e.target.value)}
                            onPressEnter={() => this.loadRecords(1)}
                        />
                    </FilterField>
                    <FilterField label="Tipo de usuario" className="w-[170px]">
                        <Select
                            allowClear={!readOnly}
                            disabled={readOnly}
                            placeholder="Todos"
                            value={readOnly ? 'auditor' : recordUserTypeFilter}
                            onChange={(v) => this.setRecordUserTypeFilter(v)}
                            options={[
                                { value: 'auditor', label: 'Auditor' },
                                { value: 'promotor', label: 'Promotor' },
                                { value: 'asesor', label: 'Asesor' },
                            ]}
                        />
                    </FilterField>
                    <FilterField label="Usuario" className="w-full sm:w-[240px]">
                        <Select
                            allowClear
                            showSearch
                            placeholder="Todos"
                            value={recordAuditorFilter}
                            onChange={(v) => this.setRecordAuditorFilter(v)}
                            optionFilterProp="label"
                            options={auditorsList.map((a) => ({
                                value: a._id,
                                label: `${a.name || a.email || a._id}${a.roleLabel ? ` · ${a.roleLabel}` : ''}`,
                            }))}
                        />
                    </FilterField>
                </FilterBar>

                {recordsLoading && !records.length ? (
                    <TableSkeleton rows={8} columns={8} />
                ) : (
                    <SectionCard
                        title="Registros (escaneos)"
                        description={
                            readOnly
                                ? 'Escaneos realizados por auditores · haz clic en una fila para ver el detalle'
                                : `${recordsPagination.total ?? records.length} escaneos · haz clic en una fila para ver el detalle`
                        }
                        icon={<ScanBarcode />}
                        flush
                        extra={bulkActions}>
                        <Table
                            rowKey="_id"
                            size="middle"
                            loading={recordsLoading}
                            dataSource={records}
                            columns={recordsColumns}
                            scroll={{ x: 'max-content' }}
                            rowSelection={
                                readOnly
                                    ? undefined
                                    : {
                                          selectedRowKeys: selectedRecordIds,
                                          onChange: this.toggleRecordSelection,
                                      }
                            }
                            rowClassName={() => 'cursor-pointer'}
                            onRow={(r) => ({ onClick: () => this.openRecordDrawer(r) })}
                            locale={{
                                emptyText: <EmptyState icon={<ScanBarcode />} title="Sin escaneos" description="No hay escaneos con los filtros aplicados" />,
                            }}
                            pagination={{
                                current: recordsPagination.page,
                                pageSize: recordsPagination.limit,
                                total: recordsPagination.total,
                                showSizeChanger: true,
                                pageSizeOptions: PAGE_SIZES,
                                onChange: (page, pageSize) => this.changeRecordsPage(page, pageSize),
                            }}
                        />
                    </SectionCard>
                )}
            </div>
        );
    }

    renderChecksTab() {
        const readOnly = this.readOnly;
        const { storeChecks, storeChecksLoading, storeChecksPagination } = this.state;

        const checksColumns = [
            {
                title: 'Tienda',
                ellipsis: true,
                render: (_, r) => <span className="text-sm font-medium text-surface-900">{r.storeId?.name || '—'}</span>,
            },
            {
                title: 'Auditor',
                width: 200,
                render: (_, r) => (
                    <div>
                        <div className="text-sm text-surface-900">{r.userId?.name || '—'}</div>
                        <div className="text-xs text-surface-500">{r.userId?.userType || ''}</div>
                    </div>
                ),
            },
            {
                title: <HelpTitle title="Auditados" tooltip="Escaneos reales registrados" />,
                dataIndex: 'audited',
                width: 110,
                align: 'right',
                render: (v) => <span className="tabular-nums">{v}</span>,
            },
            {
                title: <HelpTitle title="Total prod." tooltip="Total reportado por el auditor (encabezado de la visita)" />,
                dataIndex: 'totalProducts',
                width: 120,
                align: 'right',
                render: (v) => <span className="tabular-nums">{v}</span>,
            },
            { title: 'Imágenes', dataIndex: 'totalImages', width: 100, align: 'right', render: (v) => <span className="tabular-nums">{v}</span> },
            { title: 'Inicio', width: 150, render: (_, r) => <span className="text-xs text-surface-600 tabular-nums">{fmtVZ(r.startTime)}</span> },
            { title: 'Fin', width: 150, render: (_, r) => <span className="text-xs text-surface-600 tabular-nums">{fmtVZ(r.endTime)}</span> },
            { title: 'Creado', width: 150, render: (_, r) => <span className="text-xs text-surface-500 tabular-nums">{fmtVZ(r.createdAt)}</span> },
            !readOnly && {
                title: '',
                width: 56,
                align: 'right',
                render: (_, r) => (
                    <ConfirmIconButton
                        tooltip="Eliminar visita"
                        icon={<Trash2 />}
                        title="¿Eliminar visita completa?"
                        description={`Se borrarán la visita a ${r.storeId?.name || 'la tienda'} y todos sus escaneos.`}
                        onConfirm={() => this._deleteStoreCheck(r._id)}
                        okText="Sí, eliminar"
                    />
                ),
            },
        ].filter(Boolean);

        if (storeChecksLoading && !storeChecks.length) return <TableSkeleton rows={8} columns={8} />;

        return (
            <SectionCard
                title="Visitas por tienda"
                description={`${storeChecksPagination.total ?? storeChecks.length} visitas · haz clic en una fila para ver sus escaneos`}
                icon={<Store />}
                flush>
                <Table
                    rowKey="_id"
                    size="middle"
                    loading={storeChecksLoading}
                    dataSource={storeChecks}
                    columns={checksColumns}
                    scroll={{ x: 1100 }}
                    rowClassName={() => 'cursor-pointer'}
                    onRow={(r) => ({ onClick: () => this.openStoreCheckDrawer(r) })}
                    locale={{ emptyText: <EmptyState icon={<Store />} title="Sin visitas" description="Aún no hay visitas registradas" /> }}
                    pagination={{
                        current: storeChecksPagination.page,
                        pageSize: storeChecksPagination.limit,
                        total: storeChecksPagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: PAGE_SIZES,
                        onChange: (page, pageSize) => this.changeStoreChecksPage(page, pageSize),
                    }}
                />
            </SectionCard>
        );
    }

    renderReviewTab() {
        const { reviewQueue, reviewQueueLoading, reviewSelectedIds, bulkLoading, bulkCategory, bulkSegment, reviewFocusIndex } = this.state;
        const noneSelected = !reviewSelectedIds.length;

        return (
            <div className="space-y-4">
                <div className="shelf-card px-4 py-3">
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
                        <div className="flex flex-wrap items-center gap-2">
                            <Button type="primary" icon={<Check />} loading={bulkLoading} onClick={() => this.bulkApproveSelected()}>
                                Aprobar ({reviewSelectedIds.length})
                            </Button>
                            <Button icon={<Clock />} loading={bulkLoading} onClick={() => this.bulkKeepPending('Mantener pendiente por admin')}>
                                Mantener pendiente
                            </Button>
                            <Popconfirm
                                title={`¿Rechazar ${reviewSelectedIds.length} producto(s)?`}
                                description="Los productos rechazados se archivan y salen del catálogo."
                                onConfirm={() => this.bulkRejectSelected()}
                                okText="Sí, rechazar"
                                okType="danger"
                                cancelText="Cancelar"
                                disabled={noneSelected}
                                getPopupContainer={() => document.body}>
                                <Button danger icon={<X />} loading={bulkLoading} disabled={noneSelected}>
                                    Rechazar ({reviewSelectedIds.length})
                                </Button>
                            </Popconfirm>
                        </div>

                        <div className="hidden h-6 w-px bg-surface-200 lg:block" />

                        <div className="flex flex-wrap items-center gap-2">
                            <Input
                                placeholder="Categoría batch"
                                className="w-[150px]"
                                value={bulkCategory}
                                onChange={(e) => this.setBulkCategory(e.target.value)}
                                disabled={noneSelected}
                            />
                            <Input
                                placeholder="Segmento batch"
                                className="w-[150px]"
                                value={bulkSegment}
                                onChange={(e) => this.setBulkSegment(e.target.value)}
                                disabled={noneSelected}
                            />
                            <Button icon={<Tags />} loading={bulkLoading} disabled={noneSelected} onClick={() => this.bulkAssignTaxonomy(bulkCategory, bulkSegment)}>
                                Asignar taxonomía
                            </Button>
                        </div>

                        <Button icon={<Download />} onClick={() => this.exportReviewCsv()} className="lg:ml-auto">
                            Exportar CSV
                        </Button>
                    </div>
                    <div className="mt-3 flex items-center gap-1.5 border-t border-surface-100 pt-3 text-xs text-surface-500">
                        <Keyboard className="text-surface-400 text-[13px]" />
                        <span>Atajos: j/k navegar · a aprobar · r rechazar · e editar</span>
                    </div>
                </div>

                {reviewQueueLoading && !reviewQueue.length ? (
                    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <div key={i} className="shelf-card p-4">
                                <Skeleton avatar={{ shape: 'square', size: 64 }} active paragraph={{ rows: 3 }} />
                            </div>
                        ))}
                    </div>
                ) : reviewQueue.length ? (
                    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
                        {reviewQueue.map((item, idx) => (
                            <div
                                key={item.product?._id || idx}
                                className={`rounded-xl ${idx === reviewFocusIndex ? 'ring-2 ring-primary-400 ring-offset-2 ring-offset-surface-50' : ''}`}>
                                <AuditReviewCard
                                    item={item}
                                    selected={reviewSelectedIds.includes(item.product?._id)}
                                    onSelect={this.toggleReviewSelection}
                                    onEdit={this.openProductDrawer}
                                    onApprove={this.approveReviewItem.bind(this)}
                                    onReject={this.rejectReviewItem.bind(this)}
                                    onRecordClick={this.openRecordDrawer}
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="shelf-card">
                        <EmptyState
                            icon={<ListChecks />}
                            title="Cola de revisión vacía"
                            description="No hay productos pendientes de revisión"
                            action={
                                <Button icon={<RotateCw />} onClick={() => this.loadReviewQueue(1)}>
                                    Recargar cola
                                </Button>
                            }
                        />
                    </div>
                )}
            </div>
        );
    }

    renderAuditorsTab() {
        const { analytics } = this.state;
        return (
            <SectionCard
                title="Auditores más activos"
                description="Ranking de auditores de campo en los últimos 30 días. Haz clic en un auditor para ver su perfil y actividad."
                icon={<Users />}
                flush>
                <AuditorRankings auditors={analytics?.topAuditorsLast30Days || []} onAuditorClick={(id) => this.openAuditorDrawer(id)} />
            </SectionCard>
        );
    }

    render() {
        const {
            tabKey,
            productDrawerOpen,
            selectedAuditProduct,
            savingProduct,
            recordDrawerOpen,
            selectedRecordId,
            storeCheckDrawerOpen,
            selectedStoreCheckId,
            auditorDrawerOpen,
            selectedAuditorId,
            mergeModalOpen,
            mergeGroup,
            merging,
            deleting,
            correctingCurrency,
        } = this.state;

        const readOnly = this.readOnly;

        const breadcrumbItems = [{ title: 'Consola de auditoría' }];
        if (selectedAuditorId) breadcrumbItems.push({ title: 'Auditor' });
        if (selectedStoreCheckId && storeCheckDrawerOpen) breadcrumbItems.push({ title: 'Visita' });

        const allTabs = [
            { key: 'summary', icon: <ChartColumn />, label: 'Resumen', render: () => this.renderSummaryTab() },
            { key: 'products', icon: <Database />, label: 'Productos auditados', render: () => this.renderProductsTab() },
            { key: 'records', icon: <ScanBarcode />, label: 'Registros (escaneos)', render: () => this.renderRecordsTab() },
            { key: 'checks', icon: <Store />, label: 'Visitas por tienda', render: () => this.renderChecksTab() },
            { key: 'review', icon: <ListChecks />, label: 'Cola de revisión', render: () => this.renderReviewTab() },
            { key: 'auditors', icon: <Users />, label: 'Auditores', render: () => this.renderAuditorsTab() },
        ];
        // Análisis Auditor: visits first (their landing tab), then scans — nothing else.
        const tabItems = (readOnly ? ['checks', 'records'].map((k) => allTabs.find((t) => t.key === k)) : allTabs).map(({ render, ...tab }) => ({
            ...tab,
            children: render(),
        }));

        return (
            <Layout current="audit-console" user={this.user}>
                {this.props.contextHolder}
                <Breadcrumb className="mb-3 text-xs" items={breadcrumbItems} />
                <PageHeader
                    icon={<ClipboardCheck />}
                    title="Consola de auditoría"
                    className="!mb-4"
                    description={
                        readOnly
                            ? 'Visitas y escaneos realizados por auditores (solo lectura)'
                            : 'Verificación visual, control de calidad y revisión operativa de escaneos'
                    }
                    meta={
                        readOnly && (
                            <StatusTag tone="neutral" icon={<Eye />}>
                                Solo lectura
                            </StatusTag>
                        )
                    }
                />

                <Tabs activeKey={tabKey} onChange={this.setTabKey} items={tabItems} />

                <AuditProductDrawer
                    open={productDrawerOpen}
                    onClose={this.closeProductDrawer}
                    product={selectedAuditProduct}
                    onSave={(values) => this.saveAuditProduct(values)}
                    saving={savingProduct}
                    onRecordClick={this.openRecordDrawer}
                    onDelete={(p) => this._deleteProduct(p._id, false)}
                    onForceDelete={(p) => this._deleteProduct(p._id, true)}
                    deleting={deleting}
                />

                <AuditRecordDrawer
                    open={recordDrawerOpen}
                    onClose={this.closeRecordDrawer}
                    recordId={selectedRecordId}
                    onFetchRecord={this.fetchRecordById}
                    onDelete={readOnly ? undefined : (record) => this._deleteRecord(record._id)}
                    onCorrectCurrency={readOnly ? undefined : this.correctRecordCurrencyToBs}
                    deleting={deleting}
                    correctingCurrency={correctingCurrency}
                />

                <AuditStoreCheckDrawer
                    open={storeCheckDrawerOpen}
                    onClose={this.closeStoreCheckDrawer}
                    storeCheckId={selectedStoreCheckId}
                    onFetchRecords={this.fetchStoreCheckRecords}
                    onRecordClick={this.openRecordDrawer}
                    onDelete={readOnly ? undefined : (sc) => this._deleteStoreCheck(sc._id)}
                    deleting={deleting}
                />

                <AuditAuditorDrawer
                    open={auditorDrawerOpen}
                    onClose={this.closeAuditorDrawer}
                    userId={selectedAuditorId}
                    onFetchStats={this.fetchAuditorStats}
                    onFetchActivity={this.fetchAuditorActivity}
                    onVisitClick={(visit) => this.openStoreCheckDrawer(visit)}
                />

                <AuditMergeModal
                    open={mergeModalOpen}
                    onClose={this.closeMergeModal}
                    group={mergeGroup}
                    onMerge={this.mergeProducts.bind(this)}
                    merging={merging}
                />
            </Layout>
        );
    }
}

export default withNavigate(ShelfAuditConsole);
